import { isUs } from './dates'

// Address wording and layout by region. The stored fields are the same everywhere
// (addressLine1/2, city, county, postcode, country): for US tenants `county` holds the state and
// `postcode` the ZIP code, so no data migration is needed - only labels and layout change.

export type AddressLabels = {
  county: string
  countyPlaceholder: string
  postcode: string
  postcodePlaceholder: string
  countryPlaceholder: string
  /** Hint for the address search box */
  search: string
}

export function addressLabels(region?: string | null): AddressLabels {
  return isUs(region)
    ? {
        county: 'State', countyPlaceholder: 'e.g. CA',
        postcode: 'ZIP code', postcodePlaceholder: 'e.g. 94105',
        countryPlaceholder: 'e.g. United States',
        search: 'Search for an address or ZIP code…',
      }
    : {
        county: 'County', countyPlaceholder: 'e.g. Greater Manchester',
        postcode: 'Postcode', postcodePlaceholder: 'e.g. M1 1AA',
        countryPlaceholder: 'e.g. United Kingdom',
        search: 'Search for an address or postcode…',
      }
}

export type AddressParts = {
  line1?: string | null
  line2?: string | null
  city?: string | null
  county?: string | null
  postcode?: string | null
  country?: string | null
}

const present = (v?: string | null): v is string => !!v && v.trim().length > 0

/**
 * Formats an address for display. UK-style: `line1, line2, city, county, postcode, country`.
 * US-style: `line1, line2, City, ST 12345, country` (state and ZIP share a segment).
 */
export function formatAddress(parts: AddressParts, region?: string | null, separator = ', '): string {
  const { line1, line2, city, county, postcode, country } = parts
  const locality = isUs(region)
    ? [[city, [county, postcode].filter(present).join(' ')].filter(present).join(', ')]
    : [city, county, postcode]
  return [line1, line2, ...locality, country].filter(present).map(v => v.trim()).join(separator)
}
